// Encryption involves two layers. AES-GCM encryption with Symmetic secret key for encrypting the payload & RSA-OAEP with public-private key pairs (a.k.a Assymetric) for encrypting the symmetric key. RSA encryption has size constraints that prevents the use of RSA for a large payload encryption. Hence RSA will be used for encrypting only three items, 1) AES symmetric key, 2) AES iv, 3) one UUID

function buf2hex(buffer) {
  // buffer is an ArrayBuffer
  return Array.prototype.map
    .call(new Uint8Array(buffer), (x) => ("00" + x.toString(16)).slice(-2))
    .join("");
}

// from https://stackoverflow.com/a/11562550/9014097
function buf2Base64(buffer) {
  return btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)));
}

export const hashingFeatures = async (val) => {
  console.log("val", val);
  const inputBytes = new TextEncoder().encode(val);
  const hashBytes = await window.crypto.subtle.digest("SHA-256", inputBytes);
  const hax = JSON.stringify(buf2hex(hashBytes));
  return hax;
};

// Convert  an ArrayBuffer into a string from https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
const ab2str = (buf) => {
  return String.fromCharCode.apply(null, new Uint8Array(buf));
};

// Convert a string into an ArrayBuffer from https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
const str2ab = (str) => {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
};

// Encode string into a form we can use for the encrypt operation.
const getMessageForEncryption = (message) => {
  let enc = new TextEncoder();
  return enc.encode(message);
};

// Convert base64 string into an Array buffer
const getArrayBuffer = (message) => {
  // base64 decode the string to get the binary data
  const binaryDerString = window.atob(message);
  // convert from a binary string to an ArrayBuffer
  const genArrayBuffer = str2ab(binaryDerString);
  return genArrayBuffer;
};

// Generate RSA key pair
export const generateRsaKey = async () => {
  const rsaAoepKeyPair = await window.crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-256",
    },
    true,
    ["encrypt", "decrypt"]
  );
  return rsaAoepKeyPair;
};

// Export the RSA public key for sharing.
export const exportRsaCryptoKey = async (publicKey) => {
  const exported = await window.crypto.subtle.exportKey("spki", publicKey);
  const exportedAsString = ab2str(exported);
  const exportedAsBase64 = window.btoa(exportedAsString);
  return exportedAsBase64;
};

// Use base64 string to import RSA crypto Key Object
export const importRsaKey = async (publicKey) => {
  const publicKeyArrBuffer = getArrayBuffer(publicKey);
  const cryptoKeyObj = await window.crypto.subtle.importKey(
    "spki",
    publicKeyArrBuffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["encrypt"]
  );
  return cryptoKeyObj;
};

// Get the message, encoded it & encrypt using RSA-OAEP method
export const rsaEncryptMessage = async (publicKey, message) => {
  let encoded = getMessageForEncryption(message);
  const rsaCipheredtext = await window.crypto.subtle.encrypt(
    {
      name: "RSA-OAEP",
    },
    publicKey,
    encoded
  );
  const exportedAsString = ab2str(rsaCipheredtext);
  const exportedAsBase64 = window.btoa(exportedAsString);
  return exportedAsBase64;
};

// Get the RSA-OAEP encrypted text, convert to array buffer & decrypt it
export const rsaDecryptMessage = async (privateKey, message) => {
  const encryptedArrayBuffer = getArrayBuffer(message);
  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: "RSA-OAEP",
    },
    privateKey,
    encryptedArrayBuffer
  );
  const dec = new TextDecoder();
  const decryptedMessage = dec.decode(decrypted);
  return decryptedMessage;
};

// Generate AES-GCM secret key
export const generateAesKey = async () => {
  const aesGcmSecretKey = await window.crypto.subtle.generateKey(
    {
      name: "AES-GCM",
      length: 256,
    },
    true,
    ["encrypt", "decrypt"]
  );
  return aesGcmSecretKey;
};

// Export the AES-GCM secret key for sharing.
export const exportAesCryptoKey = async (key) => {
  const exported = await window.crypto.subtle.exportKey("raw", key);
  const exportedKeyBuffer = new Uint8Array(exported);
  const exportedAsString = ab2str(exportedKeyBuffer);
  const exportedAsBase64 = window.btoa(exportedAsString);
  return exportedAsBase64;
};

// Use base64 string to import AES secret Key
export const importAesKey = async (key) => {
  const rawKey = getArrayBuffer(key);
  const cryptoKeyObj = await window.crypto.subtle.importKey(
    "raw",
    rawKey,
    "AES-GCM",
    true,
    ["encrypt", "decrypt"]
  );
  return cryptoKeyObj;
};

// Get the message, encoded it & encrypt using AES-GCM method
export const aesEncryptMessage = async (key, message) => {
  let encoded = getMessageForEncryption(message);
  // iv will be needed for decryption
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ivString = ab2str(iv);
  const ivBase64 = window.btoa(ivString);
  const aesCipheredtext = await window.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    key,
    encoded
  );
  const exportedAsString = ab2str(aesCipheredtext);
  const exportedAsBase64 = window.btoa(exportedAsString);
  const aesEncryptObject = {
    aesCipheredText: exportedAsBase64,
    ivBase64,
  };
  return aesEncryptObject;
};

// Get the AES-GCM encrypted text, convert to array buffer & decrypt it
export const aesDecryptMessage = async (key, ivBase64, message) => {
  const encryptedArrayBuffer = getArrayBuffer(message);
  const iv = getArrayBuffer(ivBase64);
  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    key,
    encryptedArrayBuffer
  );
  const dec = new TextDecoder();
  const decryptedMessage = dec.decode(decrypted);
  return decryptedMessage;
};

let rsaAoepKeyPair;
let exportRsaPublicKey;

export const encryption = async (SPK, data) => {
  window.sessionStorage.setItem("serverPublicKey", SPK);
  const importedRsaCryptoKeyObj = await importRsaKey(SPK);
  const aesGcmKey = await generateAesKey();
  const exportAesSecretKey = await exportAesCryptoKey(aesGcmKey);
  const { aesCipheredText, ivBase64 } = await aesEncryptMessage(
    aesGcmKey,
    data
  );
  const headerPayload = {
    reqId: Date.now(),
    clientSymmetric: exportAesSecretKey,
    clientIv: ivBase64,
  };
  const cipheredheaderPayload = await rsaEncryptMessage(
    importedRsaCryptoKeyObj,
    JSON.stringify(headerPayload)
  );
  rsaAoepKeyPair = await generateRsaKey();
  exportRsaPublicKey = await exportRsaCryptoKey(rsaAoepKeyPair.publicKey);
  window.sessionStorage.setItem("clientPublicKey", exportRsaPublicKey);
  const header = {
    SEPRK: cipheredheaderPayload,
    CPK: exportRsaPublicKey,
  };

  const reqObj = {
    encryptedRequest: aesCipheredText,
  };

  return { header, reqObj };
};

export const decryption = async (CEPRK, response) => {
  const rsaDecrytedText = await rsaDecryptMessage(
    rsaAoepKeyPair.privateKey,
    CEPRK
  );
  const rsaDecrytedobj = JSON.parse(rsaDecrytedText);
  const importedAesCryptoKeyObj = await importAesKey(
    rsaDecrytedobj.symmetricSecret
  );
  const aesDecrytedText = await aesDecryptMessage(
    importedAesCryptoKeyObj,
    rsaDecrytedobj.responseIV,
    response
  );
  const decrytedText = JSON.parse(aesDecrytedText);
  return decrytedText;
};
